import {
  Component,
  computed,
  input,
  output,
  signal,
  OnInit,
  inject,
  ChangeDetectionStrategy,
  OnDestroy,
  ViewChild,
  effect,
} from '@angular/core';
import { MatCardModule } from '@angular/material/card';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { BytesToSizePipe } from '../../../../shared/pipes/bytes-to-size-pipe';
import {
  GroupBandwidthUsage,
  GroupCpuUsage,
  QuotaAndGroupDiskUsage,
} from '../../../../shared/models/group-overview.model';
import moment from 'moment';
import { map, shareReplay, Subject, switchMap, takeUntil } from 'rxjs';
import { WebsocketService } from '../../../../core/services/websocket.service';
import { Preset, WebsiteGroupSnapshot } from '../../../../shared/models/usage.models';
import { MatButtonModule } from '@angular/material/button';
import { InputField } from '../../../../shared/components/input-field/input-field';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { ChartConfiguration, ChartType } from 'chart.js';
import { BaseChartDirective } from 'ng2-charts';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { HostingPlan } from '../../../../shared/models/website-overview.model';
import { UsageService } from '../../../../core/services/usage.service';

@Component({
  selector: 'app-group-usage',
  imports: [
    MatCardModule,
    FontAwesomeModule,
    MatProgressBarModule,
    MatTooltipModule,
    BytesToSizePipe,
    MatButtonModule,
    InputField,
    ReactiveFormsModule,
    BaseChartDirective,
    MatProgressSpinnerModule,
  ],
  templateUrl: './group-usage.component.html',
  styleUrl: './group-usage.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GroupUsageComponent implements OnInit, OnDestroy {
  private usageService = inject(UsageService);

  private websocketService = inject(WebsocketService);

  groupId = input.required<number>();

  userId = input.required<number>();

  diskUsage = input.required<QuotaAndGroupDiskUsage>();

  cpuUsage = input.required<GroupCpuUsage>();

  bandwidthUsage = input.required<GroupBandwidthUsage>();

  plan = input.required<HostingPlan>();

  // === Start of Disk Usage ===

  newUsages = signal<{
    files?: number;
    mailboxes?: number;
    databases?: number;
  }>({});

  filesSize = computed(() => this.diskUsage().groupDiskUsageSnapshot?.groupUsage?.totalFilesSize);

  mailboxesSize = computed(
    () => this.diskUsage().groupDiskUsageSnapshot?.groupUsage?.totalMailboxesSize,
  );

  databasesSize = computed(
    () => this.diskUsage().groupDiskUsageSnapshot?.groupUsage?.totalDatabasesSize,
  );

  filesUsage = computed(() => this.newUsages().files || this.filesSize() || 0);

  mailboxesUsage = computed(() => this.newUsages().mailboxes || this.mailboxesSize() || 0);

  databasesUsage = computed(() => this.newUsages().databases || this.databasesSize() || 0);

  siteTotal = computed(() => this.diskUsage().quotaSnapshot.blockUsed + this.databasesUsage());

  currentUsage = computed(() => this.diskUsage()?.quotaSnapshot.blockUsed);

  homeDirPercentage = computed(() => {
    const quota = this.diskUsage()?.quotaSnapshot;

    if (!quota || quota.blockHard <= 0) {
      return 0;
    }

    return (quota.blockUsed / quota.blockHard) * 100;
  });

  siteTotalPercentage = computed(() => {
    const limit = this.diskUsage()?.quotaSnapshot.blockHard;

    if (limit == null || limit <= 0) {
      return 0;
    }

    return (this.siteTotal() / limit) * 100;
  });

  lastUpdated = computed(() => {
    const date = this.diskUsage()?.quotaSnapshot.date;

    return date ? moment(date * 1000).format('DD.MM.YYYY, HH:mm:ss') : 'N/A';
  });

  updateInterval = computed(() => {
    const interval = this.diskUsage()?.quotaSnapshot.interval;
    return interval ? interval / 60 : 60;
  });

  syncDowntime = computed(() => this.diskUsage()?.quotaSnapshot.interval);

  // === End of Disk Usage ===

  // === Start of Bandwidth ===

  bandwidthPreset = new FormControl<Preset>('30d');

  selectedBandwidthPeriod = toSignal(this.bandwidthPreset.valueChanges);

  bandwidthUsage$ = this.bandwidthPreset.valueChanges.pipe(
    switchMap((preset) =>
      this.usageService.getBandwidthUsageByGroup(this.groupId(), this.getStartDate(preset)),
    ),
    shareReplay(1),
  );

  bandwidthUsageFromApi = toSignal(this.bandwidthUsage$, {
    initialValue: null,
  });

  bandwidthTotalUsage = computed(
    () => this.bandwidthUsageFromApi()?.totalUsage ?? this.bandwidthUsage().totalUsage,
  );

  bandwidthLimit = computed(() => this.plan().monthlyBandwidthLimit);

  bandwidthPercent = computed(() => {
    const usage = this.bandwidthTotalUsage();
    const limit = this.bandwidthLimit();

    if (usage == null || limit == null || limit <= 0) {
      return 0;
    }

    return Math.round((usage / limit) * 10000) / 100;
  });

  // === End of bandwidth ===

  // === Start of CPU ===

  periodOptions: { label: string; value: Preset }[] = [
    {
      label: 'Last 24 hours',
      value: '24h',
    },
    {
      label: 'Last 7 days',
      value: '7d',
    },
    {
      label: 'Last 30 days',
      value: '30d',
    },
  ];

  cpuPreset = new FormControl<Preset>('30d');

  selectedPeriod = toSignal(this.cpuPreset.valueChanges);

  cpuUsage$ = this.cpuPreset.valueChanges.pipe(
    switchMap((preset) => this.usageService.getCpuUsage(this.userId().toString(), preset || '30d')),
    map((u) => u.points.map((p) => ({ timestamp: p.timestampUnixSeconds, usage: p.value }))),
  );

  cpuUsageFromAPI = toSignal(this.cpuUsage$);

  cpuComputedUsage = computed(() => this.cpuUsageFromAPI() ?? this.cpuUsage().usage);

  lineChartData: ChartConfiguration['data'] = {
    datasets: [
      {
        data: [100, 200],
        label: 'Usage',
        backgroundColor: '#322b42',
        borderWidth: 4,
        borderColor: '#a78bfa',
        pointBackgroundColor: '#776a93',
        pointBorderColor: '#a78bfa',
        pointHoverBackgroundColor: '#d7cfe5',
        pointHoverBorderColor: '#a290c9',
        fill: 'origin',
      },
    ],
    labels: ['1 Jan', '31 Jan'],
  };

  lineChartOptions: ChartConfiguration['options'] = {
    responsive: true,
    maintainAspectRatio: false,
    elements: {
      line: {
        tension: 0.5,
      },
    },
    scales: {
      // We use this empty structure as a placeholder for dynamic theming.
      y: {
        position: 'left',
      },
      y1: {
        position: 'right',
        grid: {
          display: false,
        },
        ticks: {
          display: false,
        },
      },
    },

    plugins: {
      legend: { display: false },
    },
  };

  lineChartType: ChartType = 'line';

  @ViewChild(BaseChartDirective) chart?: BaseChartDirective;

  // === End of CPU ===

  syncUsage = output();

  isDisabled = signal(false);

  destroyed$ = new Subject();

  constructor() {
    effect(() => {
      this.lineChartData.datasets[0].data = this.cpuComputedUsage().map(
        (p) => +p.usage / 1024 / 1024,
      );
      this.lineChartData.labels = this.cpuComputedUsage().map((p) =>
        moment.unix(p.timestamp).format(this.selectedPeriod() === '24h' ? 'HH:mm' : 'DD MMM'),
      );
      this.chart?.update();
    });
  }

  ngOnInit(): void {
    const snapshot = this.diskUsage().groupDiskUsageSnapshot;
    if (snapshot) {
      const remainingDowntime = (snapshot.date + snapshot.interval) * 1000 - Date.now();
      if (remainingDowntime > 0) {
        this.isDisabled.set(true);
        setTimeout(() => {
          this.isDisabled.set(false);
        }, remainingDowntime);
      }
    }

    this.websocketService
      .listen<{ payload: WebsiteGroupSnapshot }>('websiteGroupUsageUpdated')
      .pipe(takeUntil(this.destroyed$))
      .subscribe((data) => {
        // const websiteUsage = data.payload.websiteUsage.find(
        //   (w) => w.websiteId === this.websiteData().website.id,
        // );
        // const newFilesUsage = websiteUsage?.size;
        // const newMailboxUsage = websiteUsage?.mailboxUsage.reduce(
        //   (acc, curr) => acc + curr.blockUsed,
        //   0,
        // );
        // const newDatabaseUsage = websiteUsage?.databasesUsage.reduce(
        //   (acc, curr) => acc + curr.size,
        //   0,
        // );
        // this.newUsages.set({
        //   files: newFilesUsage,
        //   mailboxes: newMailboxUsage,
        //   databases: newDatabaseUsage,
        // });
        console.log(data);
      });
  }

  syncDiskUsage(): void {
    this.isDisabled.set(true);
    this.syncUsage.emit();

    const downtime = this.syncDowntime();
    if (downtime)
      setTimeout(() => {
        this.isDisabled.set(false);
      }, downtime * 1000);
  }

  getStartDate(preset: Preset | null): number {
    const currentDate = moment().utc();
    switch (preset) {
      case '24h':
        return currentDate.startOf('day').unix();
      case '7d':
        return currentDate.startOf('week').unix();
      default:
        return currentDate.startOf('month').unix();
    }
  }

  ngOnDestroy(): void {
    this.destroyed$.next('');
    this.destroyed$.complete();
  }
}
